export const toSelectOptions = (
  records: any[],
  getLabel: (record: any) => string,
  getValue: (record: any) => string | number = (record) => record.id,
) =>
  records.filter(Boolean).map((record) => ({
    label: getLabel(record),
    value: getValue(record),
  }));

export type TeamSportScope = {
  schoolSportId: string;
  sportId: string;
  sportName: string;
};

const normalizeIds = (values: Array<string | number | null | undefined>) =>
  values.filter(Boolean).map((value) => String(value));

export const resolveTeamSportScope = (team?: any, schoolSports: any[] = []): TeamSportScope => {
  const schoolSportId = String(team?.school_sport_id || team?.school_sport?.id || "");
  const sportId = String(team?.sport_id || team?.school_sport?.sport_id || "");
  const matchedSchoolSport =
    schoolSports.find((entry) => String(entry.id) === schoolSportId) ||
    schoolSports.find((entry) => String(entry.sport_id) === sportId);

  return {
    schoolSportId: schoolSportId || String(matchedSchoolSport?.id || ""),
    sportId: sportId || String(matchedSchoolSport?.sport_id || ""),
    sportName: team?.sport_name || matchedSchoolSport?.sport_name || "",
  };
};

export const isSchoolPlayerRegisteredForTeamSport = (
  player: any,
  scope: TeamSportScope,
): boolean => {
  if (!scope.schoolSportId && !scope.sportId && !scope.sportName) {
    return true;
  }

  const registrations = player?.school_sports || player?.school_sport_registrations || [];
  if (registrations.length > 0) {
    return registrations.some((registration: any) => {
      const registrationSchoolSportIds = normalizeIds([
        registration?.id,
        registration?.school_sport_id,
      ]);
      const registrationSportIds = normalizeIds([registration?.sport_id]);

      if (scope.schoolSportId && registrationSchoolSportIds.includes(scope.schoolSportId)) {
        return true;
      }

      if (scope.sportId && registrationSportIds.includes(scope.sportId)) {
        return true;
      }

      if (
        scope.sportName &&
        String(registration?.name || registration?.sport_name || "").toLowerCase() ===
          scope.sportName.toLowerCase()
      ) {
        return true;
      }

      return false;
    });
  }

  const schoolSportIds = normalizeIds(player?.school_sport_ids || []);
  if (scope.schoolSportId && schoolSportIds.includes(scope.schoolSportId)) {
    return true;
  }

  const sportIds = normalizeIds([...(player?.sport_ids || []), player?.sport_id]);
  if (scope.sportId && sportIds.includes(scope.sportId)) {
    return true;
  }

  const sportNames = [...(player?.school_sport_names || []), ...(player?.sport_names || [])].map(
    (name) => String(name).toLowerCase(),
  );

  if (scope.sportName && sportNames.includes(scope.sportName.toLowerCase())) {
    return true;
  }

  const linkedTeams = player?.school_teams || [];
  if (linkedTeams.length > 0) {
    return linkedTeams.some((linkedTeam: any) => {
      const linkedSchoolSportIds = normalizeIds([
        linkedTeam?.school_sport_id,
        linkedTeam?.school_sport?.id,
      ]);
      const linkedSportIds = normalizeIds([linkedTeam?.sport_id, linkedTeam?.school_sport?.sport_id]);

      if (scope.schoolSportId && linkedSchoolSportIds.includes(scope.schoolSportId)) {
        return true;
      }

      if (scope.sportId && linkedSportIds.includes(scope.sportId)) {
        return true;
      }

      if (
        scope.sportName &&
        String(linkedTeam?.sport_name || "").toLowerCase() === scope.sportName.toLowerCase()
      ) {
        return true;
      }

      return false;
    });
  }

  return false;
};

export const filterAssignableSchoolTeamPlayers = ({
  players,
  resolvedSchoolId,
  roster,
  schoolSports = [],
  team,
}: {
  players: any[];
  resolvedSchoolId: string;
  roster: any[];
  schoolSports?: any[];
  team?: any;
}) => {
  const scope = resolveTeamSportScope(team, schoolSports);
  const rosterIds = new Set(roster.map((player) => String(player.id)));

  return players
    .filter((player) => {
      if (player?.school_id && String(player.school_id) !== String(resolvedSchoolId)) {
        return false;
      }

      return isSchoolPlayerRegisteredForTeamSport(player, scope);
    })
    .filter((player) => !rosterIds.has(String(player.id)));
};

export const schoolDocumentManageActions = [
  "school_documents.manage",
  "school_documents.create",
  "school_documents.update",
  "school_documents.delete",
  "schools.update",
  "schools.manage",
  "school_matches.manage",
  "school_teams.update",
  "school_team_players.manage",
  "school_players.create",
];

export const schoolDocumentVerifyActions = [
  "school_documents.verify",
  "school_documents.manage",
  "schools.update",
  "schools.manage",
  "school_matches.manage",
];

export const schoolGalleryManageActions = [
  "school_gallery.manage",
  "schools.update",
  "schools.manage",
];

export const schoolTeamGalleryManageActions = [
  "school_team_gallery.manage",
  "school_teams.update",
  "school_teams.manage",
];
